import api from './api';
import { rbacRolesEntity } from '@/config/entities/rbacRoles';

import { CRUDService, DataItem } from '@/components/common/CRUDTable/types';

/**
 * RBAC Roles CRUD Service
 * 
 * Handles CRUD operations for RBAC roles using the flexible CRUD framework.
 * This service provides role-specific functionality.
 */
export class RBACRolesCRUDService implements CRUDService<DataItem> {
  private entity = rbacRolesEntity;

  /**
   * Get all roles with additional role-specific processing
   */
  async getAll(searchTerm?: string): Promise<DataItem[]> {
    try {
      const response = await api.get(this.entity.apiEndpoint, {
        params: { search: searchTerm }
      });

      // Transform the response to match DataItem interface
      const roles = response.data.data.map((role: any) => ({
        id: role.id || '', // Use actual database ID
        role: role.role_name || role.role,
        name: role.role_name || role.role,
        description: role.description || '',
        permissions: role.permissions || [],
        isSystemRole: role.isSystemRole || false,
        canBeModified: role.canBeModified !== false,
        tenantId: role.tenant_id,
        isActive: role.isActive !== false
      }));

      return roles;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }

  /**
   * Get a specific role by ID
   */
  async getById(id: string): Promise<DataItem | null> {
    try {
      const response = await api.get(`${this.entity.apiEndpoint}/${id}`);
      
      const role = response.data.data;
      return {
        id: role.id || '', // Use actual database ID
        role: role.role_name || role.role,
        name: role.role_name || role.role,
        description: role.description || '',
        permissions: role.permissions || [],
        isSystemRole: role.isSystemRole || false,
        canBeModified: role.canBeModified !== false,
        tenantId: role.tenant_id,
        isActive: role.isActive !== false
      };
    } catch (error) {
      console.error('Error fetching role:', error);
      throw error;
    }
  }

  /**
   * Create a new role
   */
  async create(data: Partial<DataItem>): Promise<DataItem> {
    try {
      const response = await api.post(this.entity.apiEndpoint, data);
      
      const role = response.data.data;
      return {
        id: role.id || '', // Use actual database ID
        role: role.role_name || role.role,
        name: role.role_name || role.role,
        description: role.description || '',
        permissions: role.permissions || [],
        isSystemRole: role.isSystemRole || false,
        canBeModified: role.canBeModified !== false,
        tenantId: role.tenant_id,
        isActive: role.isActive !== false
      };
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  }

  /**
   * Update an existing role
   */
  async update(id: string, data: Partial<DataItem>): Promise<DataItem> {
    try {
      const response = await api.put(`${this.entity.apiEndpoint}/${id}`, data);
      
      const role = response.data.data;
      return {
        id: role.id || '', // Use actual database ID
        role: role.role_name || role.role,
        name: role.role_name || role.role,
        description: role.description || '',
        permissions: role.permissions || [],
        isSystemRole: role.isSystemRole || false,
        canBeModified: role.canBeModified !== false,
        tenantId: role.tenant_id,
        isActive: role.isActive !== false
      };
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  }

  /**
   * Delete a role
   */
  async delete(id: string): Promise<void> {
    try {
      await api.delete(`${this.entity.apiEndpoint}/${id}`);
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  }

  /**
   * Toggle the active status of a role
   */
  async toggleStatus(id: string, isActive: boolean): Promise<void> {
    try {
      await api.patch(`${this.entity.apiEndpoint}/${id}/status`, {
        isActive
      });
    } catch (error) {
      console.error('Error toggling role status:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const rbacRolesCRUDService = new RBACRolesCRUDService();
